import type { Agent, AgentRuntimeStatus, Persisted } from '@tunasong/schemas'

/**
 * Client-side TTL for a `thinking` broadcast that never resolved
 * (e.g. the Lambda died mid-turn).
 *
 * Chosen as: roughly the longest reasonable agent turn (~60s for
 * agent-stream Lambda max) + WS round-trip jitter. Tune after live data
 * shows real distribution.
 */
export const RUNTIME_STATUS_STALE_MS = 90_000

interface ClockDeps {
  now?: () => number
}

/**
 * Derived live runtime status for an agent. Pure function — no React
 * state, no timers, no re-render side-effects. Renders are recomputed
 * any time the agent entity updates in the graph cache; the staleness
 * fallback applies as a render-time derivation.
 *
 * If a stuck `thinking` is older than RUNTIME_STATUS_STALE_MS, this
 * collapses to `idle`. The visual treatment for the stale window
 * (e.g. a softer "took too long" badge) is rendered upstream when the
 * panel is open — out of scope here.
 */
export const useAgentRuntimeStatus = (
  agent: Pick<Persisted<Agent>, 'runtimeStatus' | 'runtimeStatusAt'>,
  deps: ClockDeps = {}
): AgentRuntimeStatus => {
  const status = agent.runtimeStatus
  if (!status || status === 'idle') {
    return 'idle'
  }
  if (status === 'thinking' && agent.runtimeStatusAt) {
    const now = deps.now ? deps.now() : Date.now()
    const ageMs = now - new Date(agent.runtimeStatusAt).getTime()
    if (Number.isFinite(ageMs) && ageMs > RUNTIME_STATUS_STALE_MS) {
      return 'idle'
    }
  }
  return status
}
